import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Instagram, ArrowUpRight, Star } from "lucide-react";
import { BRAND } from "@/lib/constants";

const PRODUCTS = [
  {
    id: "sw1",
    name: "Personalised Welcome Sign",
    img: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/5zfsxqqn_WhatsApp%20Image%202026-02-14%20at%2023.56.46%20-%20Copy.jpeg",
    tag: "Bestseller",
  },
  {
    id: "sw2",
    name: "Custom Wedding Playing Cards",
    img: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/1zxndhd7_WhatsApp%20Image%202026-02-22%20at%2023.47.27.jpeg",
    tag: "Fun Favour",
  },
  {
    id: "sw3",
    name: "Wedding Day Newspaper",
    img: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/w40hkg7j_WhatsApp%20Image%202026-03-04%20at%2020.03.19%20-%20Copy.jpeg",
    tag: "Editorial",
  },
  {
    id: "sw4",
    name: "Petal Toss Cones",
    img: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/hd0c5ccr_WhatsApp%20Image%202026-03-04%20at%2020.03.21%20-%20Copy.jpeg",
    tag: "Set",
  },
  {
    id: "sw5",
    name: "Cutout Itinerary Cards",
    img: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/bmaqk4wu_WhatsApp%20Image%202026-04-18%20at%2014.09.08.jpeg",
    tag: "Handcrafted",
  },
  {
    id: "sw6",
    name: "Luggage Tags",
    img: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/h5dd1tf7_WhatsApp%20Image%202026-04-18%20at%2014.09.09.jpeg",
    tag: "Destination Wedding",
  },
];

export default function StationeryWalaPopup({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[130] grid place-items-center p-4 md:p-8"
          style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(14px)" }}
          onClick={onClose}
          data-testid="stationery-popup"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto"
            style={{
              background:
                "linear-gradient(180deg, #1a1416 0%, #141014 100%)",
              border: "1px solid rgba(212,175,55,0.35)",
              boxShadow:
                "0 40px 100px -10px rgba(232,165,180,0.35), inset 0 0 0 1px rgba(255,255,255,0.03)",
            }}
          >
            {/* Close */}
            <button
              onClick={onClose}
              aria-label="Close"
              data-testid="stationery-popup-close"
              className="absolute top-4 right-4 z-10 w-10 h-10 grid place-items-center border border-white/20 text-[var(--iw-ivory)] hover:border-[var(--iw-gold)] hover:text-[var(--iw-gold)] transition-colors"
            >
              <X size={18} />
            </button>

            {/* Header */}
            <div className="relative p-6 md:p-10 pb-0 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10">
              <img
                src={BRAND.sister.logo}
                alt={`${BRAND.sister.name} — sister brand of InvitationWala for printed wedding stationery`}
                className="h-20 md:h-24 w-auto object-contain shrink-0"
                width="160"
                height="96"
                loading="lazy"
                decoding="async"
              />
              <div>
                <div className="overline text-[var(--iw-gold)] mb-2">
                  Our Sister Brand
                </div>
                <h3
                  className="font-serif-display text-[var(--iw-ivory)] text-3xl md:text-5xl leading-tight"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  Pixels, meet{" "}
                  <span className="italic gold-rose-gradient">paper</span>.
                </h3>
                <p className="text-[var(--iw-muted)] text-sm md:text-base mt-3 max-w-lg leading-relaxed">
                  When your invite deserves to be held in hand — not just seen
                  on a screen. Printed, foiled, letterpressed, and delivered to
                  every guest's door.
                </p>
              </div>
            </div>

            {/* Categories — text-only, no product photos */}
            <div className="p-6 md:p-10">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                {PRODUCTS.map((p, i) => (
                  <motion.a
                    key={p.id}
                    href={BRAND.sister.instagramUrl}
                    target="_blank"
                    rel="noreferrer"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.6,
                      delay: 0.15 + i * 0.05,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="group p-4 md:p-5 border border-white/10 hover:border-[var(--iw-gold)]/50 transition-colors flex items-start justify-between gap-3"
                    style={{ background: "rgba(255,255,255,0.02)" }}
                    data-testid={`stationery-product-${p.id}`}
                  >
                    <div>
                      <span
                        className="overline text-[10px] mb-1.5 block"
                        style={{ color: "var(--iw-gold)" }}
                      >
                        {p.tag}
                      </span>
                      <div
                        className="font-serif-display text-[var(--iw-ivory)] text-lg md:text-xl leading-tight"
                        style={{ fontFamily: "Cormorant Garamond, serif" }}
                      >
                        {p.name}
                      </div>
                    </div>
                    <ArrowUpRight
                      size={14}
                      className="text-[var(--iw-faint)] group-hover:text-[var(--iw-gold)] shrink-0 mt-1 transition-colors"
                    />
                  </motion.a>
                ))}
              </div>

              {/* CTA bar */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.6 }}
                className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-between gap-5 p-5 md:p-6 border border-[var(--iw-gold)]/30"
                style={{ background: "rgba(212,175,55,0.06)" }}
              >
                <div className="flex items-center gap-3">
                  <Star size={14} className="text-[var(--iw-rose)]" fill="currentColor" />
                  <span className="overline text-[var(--iw-ivory-dim)]">
                    Tap any product to DM us on Instagram for a quote
                  </span>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <a
                    href={BRAND.sister.instagramUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-gold flex-1 sm:flex-none justify-center"
                    data-testid="stationery-popup-ig"
                  >
                    <Instagram size={14} /> @stationery_wala__
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
